/**
 * Custom Icons Configuration
 *
 * This file defines:
 * - Automatic import of SVG icons from the `src/assets/icons/` directory.
 * - Mapped Custom aliases (`custom:*`)
 * - A Vuetify-compatible wrapper component for rending the icons.
 *
 * Why the `custom` prefix?
 *   To avoid conflicts with other icon sets, allowing us to register the
 *   icons easily with Vuetify Icon aliasing. However with custom icons, you also have the
 *   $alias available by default for easier usage.
 *
 * Usage examples:
 *   <v-icon icon="custom:icon-name" /> -> default naming
 *   <v-icon icon="$icon-name" /> -> with alias
 *
 * * Instructions:
 * 1. Place your SVG in `src/assets/icons/`.
 * 2. Ensure the SVG has no hardcoded width/height. We will use SVGO to clean up icons in the future.
 * 3. Use 'currentColor' for dynamic styling.
 * 4. You can also add advance colors, such as `fill="rgb(var(--v-theme-white, 255, 255, 255))"` to use css variables.
 *
 * Note: If you need to add a non-SVG icon, you can manually import and add it to the `ALL_ICONS` object.
 */
import { type Component, h } from 'vue';

import type { IconProps } from 'vuetify';

import BetterPlannerLogo from '@/contexts/depot/statistics/map/assets/BetterPlannerLogo.vue';

type ActualIconProps = IconProps & { size?: string | number };

/**
 * Auto-import all SVGs from the icons directory
 * We are using the svg-loaders `?component` query to import as Vue components
 */
const modules = import.meta.glob('@icons/**/*.svg', {
  query: '?component',
  import: 'default',
  eager: true
}) as Record<string, Component>;

/**
 * Normalize automated icons: "icon-name.svg" -> "icon-name"
 * and convert path slashes to dashes for nested icons
 */
const SVG_MAP: Record<string, Component> = Object.fromEntries(
  Object.entries(modules).map(([path, component]) => {
    const name = path
      .replace(/^.*\/icons\//, '')
      .replace(/\.svg$/, '')
      .replace(/\//g, '-');
    return [name, component];
  })
);

/**
 * Manually imported icons can be added here
 *
 * For icons with more "advance" styling, it is best to manually add it,
 * as the SVGO optimizer has issues with preserving certain styles.
 */
const ALL_ICONS: Record<string, Component> = {
  ...SVG_MAP,
  'better-planner-logo': BetterPlannerLogo
};

export const customIcons = {
  set: {
    component: (props: IconProps) => {
      const { icon: rawIcon, size = 24, ...rest } = props as ActualIconProps;

      const iconValue = rawIcon;

      if (typeof iconValue !== 'string') return null;

      // 1. Strip the prefix (matches the prefix used in aliases below)
      const name = iconValue.replace(/^custom:/, '');

      // 2. Look in ALL_ICONS (which contains both SVG_MAP and manual icons)
      const component = ALL_ICONS[name];

      if (!component) return null;

      return h(component, {
        ...props,
        ...rest,
        width: size,
        height: size
      });
    }
  },
  aliases: {
    ...(Object.fromEntries(
      Object.keys(ALL_ICONS).map((key) => [key, `custom:${key}`])
    ) as any)
  }
};

/**
 * Sorted List for UI
 *
 * Generate list of all icons in the Custom Icon list,
 * and sort it in alphabetical order, and include the alias prefix ($)
 * for easier display in the UI.
 */
export const customIconList = (Object.keys(ALL_ICONS) as string[])
  .sort((a, b) =>
    a.replace(/^custom:/, '').localeCompare(b.replace(/^custom:/, ''))
  )
  .map((iconName) => `$${iconName}`);
